import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { iif, map, Observable, of, switchMap, throwError } from 'rxjs';
import { GiftDetailsIdMappedInput, GiftView } from 'src/app/auth_profile/model/v3/transactions';
import { ColNativeV3Api } from './col-native-v3-api';
import {
  ApiLessonEventModel,
  ProductAvailabilityRequest,
  ScheduleDefinition,
  ScheduleReference,
  SimpleLessonScheduleRequest,
  SimpleProductAvailability,
  SimpleTeacherProductAvailability,
  StudentColLessonScheduleEvents,
  TeacherColLessonScheduleEvents,
} from '../../../model/v3/booking';
import { IosBridgeBase } from '../../../../mobile/api-services/bridge/ios-bridge-base';
import { LogsService } from '../../../../utils/services/logs.service';
import { HttpMethod, HttpRequest, HttpResponse, Platform } from '../../../../constraints';
import { Dates } from '../../../../utils/calendar-utils';
import { appendIfDefined, serializeDateInput } from '../../../../auth_profile/services/api/v3/rest-v3-utils';
import { Page, Pageable } from '../../../../utils/pageable';
import * as LessonsV3 from '../../../model/v3/lessons';
import {
  normalizeLessonBundlesPage,
  normalizeLessonBundle,
  normalizeLessonCommitsPage,
  normalizeLessonFlagDto,
  normalizeLessonFlagsDto,
  normalizeLessonMetricDto,
  normalizeLessonsPage,
  normalizeLessonView,
  normalizeProvaContext
} from '../../../utils/v3/lessons-v3-date-utils';

@Injectable({
  providedIn: 'root'
})
export class ColIosV3Service implements ColNativeV3Api {
  constructor(
    private readonly bridge: IosBridgeBase,
    private readonly logger: LogsService
  ) {}

  private handleResponse<T>(response: Observable<HttpResponse>): Observable<T> {
    return response.pipe(
      switchMap((res) =>
        iif(
          () => res.responseCode >= 200 && res.responseCode < 300,
          of(res.body),
          throwError(() => Error(`Error with code ${res.responseCode}`))
        )
      ),
      map((body) => (body ? JSON.parse(body) : undefined) as T)
    );
  }

  private studentUrl(schoolId: number, studentId: number, path: string = ''): string {
    return `/v1/schools/${schoolId}/students/${studentId}${path}`;
  }

  private teacherUrl(schoolId: number, teacherId: number, path: string = ''): string {
    return `/v1/schools/${schoolId}/teachers/${teacherId}${path}`;
  }

  private lessonsUrl(path: string = ''): string {
    return `/v1/lessons${path}`;
  }

  private peopleColSettingsUrl(personId: number, path: string = ''): string {
    return `/v1/people/${personId}/col-settings${path}`;
  }

  private studentBundlesUrl(schoolId: number, studentId: number, path: string = ''): string {
    return `/v1/schools/${schoolId}/students/${studentId}/bundles${path}`;
  }

  private studentCommitsUrl(schoolId: number, studentId: number, path: string = ''): string {
    return `/v1/schools/${schoolId}/students/${studentId}/commits${path}`;
  }

  private schoolProductsUrl(schoolId: number, path: string = ''): string {
    return `/v1/schools/${schoolId}/products${path}`;
  }

  private productContextsUrl(
    schoolId: number,
    studentId: number,
    productCode?: string,
    path: string = ''
  ): string {
    const productCodePath = productCode ? `/${productCode}` : '';
    return `/v1/schools/${schoolId}/students/${studentId}/product-contexts${productCodePath}${path}`;
  }

  private params(url: string, params: HttpParams): string {
    if (!params || params.keys().length === 0) return url;
    let nextUrl = url;
    let isFirst = true;
    for (const key of params.keys()) {
      const values = params.getAll(key) ?? [];
      for (const value of values) {
        nextUrl += `${isFirst ? '?' : '&'}${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`;
        isFirst = false;
      }
    }
    return nextUrl;
  }

  private appendDateRange(params: HttpParams, dateFrom?: Date | null, dateTo?: Date | null): HttpParams {
    let next = appendIfDefined(params, 'dateFrom', serializeDateInput(dateFrom));
    next = appendIfDefined(next, 'dateTo', serializeDateInput(dateTo));
    return next;
  }

  private appendPageable(params: HttpParams, pageable?: Pageable | null): HttpParams {
    if (!pageable) {
      return params;
    }
    return pageable.appendParams(params);
  }

  private appendAll(params: HttpParams, key: string, values?: string[] | null): HttpParams {
    let next = params;
    (values ?? []).forEach((value) => {
      next = next.append(key, value);
    });
    return next;
  }

  private parseEventDates<T extends { events: { eventDate: any }[] }>(items: T[]): T[] {
    return items.map((item) => {
      item.events.forEach((ev) => {
        ev.eventDate = ev.eventDate instanceof Date
          ? ev.eventDate
          : Dates.parse(String(ev.eventDate));
      });
      return item;
    });
  }

  private parseScheduleEvents<T extends { events: { eventDate: any }[] }>(schedule: T): T {
    schedule.events.forEach((ev) => {
      ev.eventDate = ev.eventDate instanceof Date
        ? ev.eventDate
        : Dates.parse(String(ev.eventDate));
    });
    return schedule;
  }

  getTeacherAvailability(
    schoolId: number,
    studentId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<SimpleTeacherProductAvailability[]> {
    this.logger.log(`(v3) getting teacher availability for schoolId=${schoolId}, studentId=${studentId}`);
    let params = this.appendDateRange(new HttpParams(), dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.studentUrl(schoolId, studentId, '/teacher-availability'), params),
      null,
      null
    );
    return this.handleResponse<SimpleTeacherProductAvailability[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getStudentLessonSchedules(
    schoolId: number,
    studentId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<StudentColLessonScheduleEvents[]> {
    this.logger.log(`(v3) getting student lesson schedules for schoolId=${schoolId}, studentId=${studentId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'lesson');
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.studentUrl(schoolId, studentId, '/schedules'), params),
      null,
      null
    );
    return this.handleResponse<StudentColLessonScheduleEvents[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  reserveColLessonSchedule(
    schoolId: number,
    studentId: number,
    requestBody: SimpleLessonScheduleRequest
  ): Observable<StudentColLessonScheduleEvents> {
    this.logger.log(`(v3) reserving col lesson schedule for schoolId=${schoolId}, studentId=${studentId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.POST,
      this.params(this.studentUrl(schoolId, studentId, '/schedules'), params),
      null,
      JSON.stringify(requestBody)
    );
    return this.handleResponse<StudentColLessonScheduleEvents>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    ).pipe(map((schedule) => this.parseScheduleEvents(schedule)));
  }

  getStudentSchedule(
    schoolId: number,
    studentId: number,
    scheduleId: number
  ): Observable<StudentColLessonScheduleEvents> {
    this.logger.log(`(v3) getting student schedule for schoolId=${schoolId}, studentId=${studentId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'lesson');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}`), params),
      null,
      null
    );
    return this.handleResponse<StudentColLessonScheduleEvents>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    ).pipe(map((schedule) => this.parseScheduleEvents(schedule)));
  }

  updateColLessonEventData(
    schoolId: number,
    studentId: number,
    scheduleId: number,
    eventId: number,
    data: ApiLessonEventModel
  ): Observable<void> {
    this.logger.log(`(v3) updating col lesson event data for schoolId=${schoolId}, studentId=${studentId}, scheduleId=${scheduleId}, eventId=${eventId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PUT,
      this.params(this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}/events/${eventId}/data`), params),
      null,
      JSON.stringify(data)
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  deleteColLesson(
    schoolId: number,
    studentId: number,
    scheduleId: number
  ): Observable<void> {
    this.logger.log(`(v3) deleting col lesson for schoolId=${schoolId}, studentId=${studentId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.DELETE,
      this.params(this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}`), params),
      null,
      null
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getAvailabilityEvents(
    schoolId: number,
    teacherId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<SimpleProductAvailability[]> {
    this.logger.log(`(v3) getting availability events for schoolId=${schoolId}, teacherId=${teacherId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.teacherUrl(schoolId, teacherId, '/schedule-events'), params),
      null,
      null
    );
    return this.handleResponse<SimpleProductAvailability[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getTeacherColLessonEvents(
    schoolId: number,
    teacherId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null
  ): Observable<TeacherColLessonScheduleEvents[]> {
    this.logger.log(`(v3) getting teacher col lesson events for schoolId=${schoolId}, teacherId=${teacherId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    params = this.appendDateRange(params, dateFrom, dateTo);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.teacherUrl(schoolId, teacherId, '/schedule-events'), params),
      null,
      null
    );
    return this.handleResponse<TeacherColLessonScheduleEvents[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getTeacherCourseProfiles(
    schoolId: number,
    teacherId: number
  ): Observable<LessonsV3.V3CourseTeacherProfile[]> {
    this.logger.log(`(v3) getting teacher course profiles for schoolId=${schoolId}, teacherId=${teacherId}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.teacherUrl(schoolId, teacherId, '/course-profiles'),
      null,
      null
    );
    return this.handleResponse<LessonsV3.V3CourseTeacherProfile[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  createAvailability(
    schoolId: number,
    teacherId: number,
    requestBody: ProductAvailabilityRequest
  ): Observable<ScheduleReference> {
    this.logger.log(`(v3) creating availability for schoolId=${schoolId}, teacherId=${teacherId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'availability');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.POST,
      this.params(this.teacherUrl(schoolId, teacherId, '/schedules'), params),
      null,
      JSON.stringify(requestBody)
    );
    return this.handleResponse<ScheduleReference>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  deleteAvailability(
    schoolId: number,
    teacherId: number,
    scheduleId: number
  ): Observable<void> {
    this.logger.log(`(v3) deleting availability for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'availability');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.DELETE,
      this.params(this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}`), params),
      null,
      null
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  deleteTeacherColLesson(
    schoolId: number,
    teacherId: number,
    scheduleId: number
  ): Observable<void> {
    this.logger.log(`(v3) deleting teacher col lesson for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.DELETE,
      this.params(this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}`), params),
      null,
      null
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  deleteAvailabilityEvent(
    schoolId: number,
    teacherId: number,
    scheduleId: number,
    eventId: number,
    deleteAll?: boolean | null
  ): Observable<void> {
    this.logger.log(`(v3) deleting availability event for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}, eventId=${eventId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = appendIfDefined(params, 'deleteAll', deleteAll);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.DELETE,
      this.params(this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}/events/${eventId}`), params),
      null,
      null
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updateAvailabilityEvent(
    schoolId: number,
    teacherId: number,
    scheduleId: number,
    eventId: number,
    definition: ScheduleDefinition,
    applyFuture?: boolean | null
  ): Observable<ScheduleDefinition> {
    this.logger.log(`(v3) updating availability event for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}, eventId=${eventId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = appendIfDefined(params, 'applyFuture', applyFuture);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PUT,
      this.params(this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}/events/${eventId}`), params),
      null,
      JSON.stringify(definition)
    );
    return this.handleResponse<ScheduleDefinition>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getStudentGifts(
    schoolId: number,
    studentId: number,
    unused?: boolean | null,
    platform?: string | null
  ): Observable<GiftView[]> {
    this.logger.log(`(v3) getting student gifts for schoolId=${schoolId}, studentId=${studentId}`);
    let params = appendIfDefined(new HttpParams(), 'unused', unused);
    params = appendIfDefined(params, 'platform', platform);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.studentUrl(schoolId, studentId, '/gifts'), params),
      null,
      null
    );
    return this.handleResponse<GiftView[]>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  createStudentGift(
    schoolId: number,
    studentId: number,
    details: GiftDetailsIdMappedInput,
    _platform?: string | null
  ): Observable<GiftDetailsIdMappedInput> {
    this.logger.log(`(v3) creating student gift for schoolId=${schoolId}, studentId=${studentId}`);
    const params = appendIfDefined(new HttpParams(), 'platform', 'EVENTS');
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.POST,
      this.params(this.studentUrl(schoolId, studentId, '/gifts'), params),
      null,
      JSON.stringify(details)
    );
    return this.handleResponse<GiftDetailsIdMappedInput>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  findLessons(
    type?: LessonsV3.LessonCategory | null,
    lessonType?: LessonsV3.LessonType | null,
    view?: string | null,
    eid?: string | null,
    studentId?: number | null,
    teacherId?: number | null,
    groupId?: number | null,
    groupEid?: string | null,
    productCode?: string | null,
    statuses?: LessonsV3.LessonStatus[] | null,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.LessonView>> {
    this.logger.log('(v3) finding lessons');
    const normalizedProductCode = productCode?.trim() ? productCode.trim() : null;
    let params = new HttpParams();
    params = appendIfDefined(params, 'type', type);
    params = appendIfDefined(params, 'lessonType', lessonType);
    params = appendIfDefined(params, 'view', view);
    params = appendIfDefined(params, 'eid', eid);
    params = appendIfDefined(params, 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    params = appendIfDefined(params, 'groupId', groupId);
    params = appendIfDefined(params, 'groupEid', groupEid);
    params = appendIfDefined(params, 'productCode', normalizedProductCode);
    params = this.appendAll(params, 'statuses', statuses ?? null);
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = this.appendPageable(params, pageable);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.lessonsUrl(), params),
      null,
      null
    );
    return this.handleResponse<Page<LessonsV3.LessonView>>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((page) => normalizeLessonsPage(page)));
  }

  getLesson(
    lessonId: number,
    view?: string | null,
    viewStudentId?: number | null
  ): Observable<LessonsV3.LessonView> {
    this.logger.log(`(v3) getting lesson lessonId=${lessonId}`);
    let params = appendIfDefined(new HttpParams(), 'view', view);
    params = appendIfDefined(params, 'viewStudentId', viewStudentId);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.params(this.lessonsUrl(`/${lessonId}`), params), null, null);
    return this.handleResponse<LessonsV3.LessonView>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((lesson) => normalizeLessonView(lesson)));
  }

  getLessonMetric(lessonId: number): Observable<LessonsV3.V3LessonMetric> {
    this.logger.log(`(v3) getting lesson metric lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.lessonsUrl(`/${lessonId}/metric`), null, null);
    return this.handleResponse<LessonsV3.V3LessonMetric>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((metric) => normalizeLessonMetricDto(metric)));
  }

  sendLessonNotification(lessonId: number): Observable<void> {
    this.logger.log(`(v3) sending lesson notification lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.POST, this.lessonsUrl(`/${lessonId}/notifications`), null, null);
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getLessonComment(
    lessonId: number,
    studentId?: number | null
  ): Observable<LessonsV3.ApiLessonMessage> {
    this.logger.log(`(v3) getting lesson comment lessonId=${lessonId}`);
    const params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.params(this.lessonsUrl(`/${lessonId}/comment`), params), null, null);
    return this.handleResponse<LessonsV3.ApiLessonMessage>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updateLessonComments(
    lessonId: number,
    message: LessonsV3.LessonMessageDto,
    studentId?: number | null
  ): Observable<LessonsV3.ApiLessonMessage> {
    this.logger.log(`(v3) updating lesson comments lessonId=${lessonId}`);
    const params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PUT,
      this.params(this.lessonsUrl(`/${lessonId}/comment`), params),
      null,
      JSON.stringify(message)
    );
    return this.handleResponse<LessonsV3.ApiLessonMessage>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getLessonFlags(lessonId: number): Observable<LessonsV3.ApiLessonFlag[]> {
    this.logger.log(`(v3) getting lesson flags lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.lessonsUrl(`/${lessonId}/flags`), null, null);
    return this.handleResponse<LessonsV3.ApiLessonFlag[]>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((flags) => normalizeLessonFlagsDto(flags)));
  }

  createLessonFlag(
    lessonId: number,
    details: LessonsV3.LessonFlagDto,
    studentId?: number | null,
    teacherId?: number | null
  ): Observable<LessonsV3.ApiLessonFlag> {
    this.logger.log(`(v3) creating lesson flag lessonId=${lessonId}`);
    let params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.POST,
      this.params(this.lessonsUrl(`/${lessonId}/flags`), params),
      null,
      JSON.stringify(details)
    );
    return this.handleResponse<LessonsV3.ApiLessonFlag>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((flag) => normalizeLessonFlagDto(flag)));
  }

  deleteLessonFlag(lessonId: number, flagId: number): Observable<void> {
    this.logger.log(`(v3) deleting lesson flag lessonId=${lessonId}, flagId=${flagId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.DELETE, this.lessonsUrl(`/${lessonId}/flags/${flagId}`), null, null);
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getLessonGroup(lessonId: number): Observable<LessonsV3.LessonGroupParticipant> {
    this.logger.log(`(v3) getting lesson group lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.lessonsUrl(`/${lessonId}/group`), null, null);
    return this.handleResponse<LessonsV3.LessonGroupParticipant>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getLessonTeacher(
    lessonId: number
  ): Observable<LessonsV3.LessonTeacherParticipant<LessonsV3.TeacherRef>> {
    this.logger.log(`(v3) getting lesson teacher lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.lessonsUrl(`/${lessonId}/teacher`), null, null);
    return this.handleResponse<LessonsV3.LessonTeacherParticipant<LessonsV3.TeacherRef>>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  getLessonStudents(
    lessonId: number
  ): Observable<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]> {
    this.logger.log(`(v3) getting lesson students lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.lessonsUrl(`/${lessonId}/students`), null, null);
    return this.handleResponse<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  getLessonStudentProgress(
    lessonId: number,
    studentNumber: number
  ): Observable<LessonsV3.V3LessonParticipantProgressDetails> {
    this.logger.log(`(v3) getting lesson student progress lessonId=${lessonId}, studentNumber=${studentNumber}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.lessonsUrl(`/${lessonId}/students/${studentNumber}/progress`),
      null,
      null
    );
    return this.handleResponse<LessonsV3.V3LessonParticipantProgressDetails>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  getLessonNotes(lessonId: number): Observable<Record<string, string>> {
    this.logger.log(`(v3) getting lesson notes lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.lessonsUrl(`/${lessonId}/notes`), null, null);
    return this.handleResponse<Record<string, string>>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getLessonProgress(lessonId: number): Observable<LessonsV3.LessonProgressDetails> {
    this.logger.log(`(v3) getting lesson progress lessonId=${lessonId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.lessonsUrl(`/${lessonId}/progress`), null, null);
    return this.handleResponse<LessonsV3.LessonProgressDetails>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updateLessonProgress(
    lessonId: number,
    progress: LessonsV3.LessonProgressDetailsDto,
    type?: LessonsV3.LessonProgressType | null
  ): Observable<LessonsV3.LessonProgressDetails> {
    this.logger.log(`(v3) updating lesson progress lessonId=${lessonId}`);
    const params = appendIfDefined(new HttpParams(), 'type', type);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PUT,
      this.params(this.lessonsUrl(`/${lessonId}/progress`), params),
      null,
      JSON.stringify(progress)
    );
    return this.handleResponse<LessonsV3.LessonProgressDetails>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updateLessonType(
    lessonId: number,
    type: LessonsV3.LessonType
  ): Observable<void> {
    this.logger.log(`(v3) updating lesson type lessonId=${lessonId}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PATCH,
      this.lessonsUrl(`/${lessonId}/type`),
      null,
      JSON.stringify(type)
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updateLessonStatus(
    lessonId: number,
    actionOrStatus: LessonsV3.LessonStatusAction | LessonsV3.LessonStatus,
    requestBody?: LessonsV3.LessonStatusRequestDto | null,
    studentId?: number | null,
    teacherId?: number | null,
    checkStatus?: boolean | null
  ): Observable<LessonsV3.LessonView | void> {
    this.logger.log(`(v3) updating lesson status lessonId=${lessonId} actionOrStatus=${actionOrStatus}`);
    const isAction = Object.values(LessonsV3.LessonStatusAction).includes(actionOrStatus as LessonsV3.LessonStatusAction);
    if (isAction) {
      let params = appendIfDefined(new HttpParams(), 'action', actionOrStatus);
      params = appendIfDefined(params, 'studentId', studentId);
      params = appendIfDefined(params, 'teacherId', teacherId);
      params = appendIfDefined(params, 'checkStatus', checkStatus);
      const request = new HttpRequest(
        Platform.Events,
        HttpMethod.PATCH,
        this.params(this.lessonsUrl(`/${lessonId}/status`), params),
        null,
        JSON.stringify(requestBody)
      );
      return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
    }
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PATCH,
      this.lessonsUrl(`/${lessonId}/status`),
      null,
      JSON.stringify(actionOrStatus)
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getPersonColSettings(personId: number): Observable<LessonsV3.PersonalSettings> {
    this.logger.log(`(v3) getting person col settings personId=${personId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.peopleColSettingsUrl(personId), null, null);
    return this.handleResponse<LessonsV3.PersonalSettings>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updatePersonColSettings(
    personId: number,
    settings: LessonsV3.PersonalSettings
  ): Observable<LessonsV3.PersonalSettings> {
    this.logger.log(`(v3) updating person col settings personId=${personId}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PUT,
      this.peopleColSettingsUrl(personId),
      null,
      JSON.stringify(settings)
    );
    return this.handleResponse<LessonsV3.PersonalSettings>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updatePersonIntroductionState(
    personId: number,
    state: LessonsV3.IntroductionState
  ): Observable<void> {
    this.logger.log(`(v3) updating person introduction state personId=${personId}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PATCH,
      this.peopleColSettingsUrl(personId, '/introduction-state'),
      null,
      JSON.stringify(state)
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getStudentBundles(
    schoolId: number,
    studentId: number,
    pageable?: Pageable | null,
    productCode?: string | null,
    available?: boolean | null
  ): Observable<Page<LessonsV3.LessonBundle>> {
    this.logger.log(`(v3) getting student bundles schoolId=${schoolId}, studentId=${studentId}`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'productCode', productCode);
    params = appendIfDefined(params, 'available', available);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.studentBundlesUrl(schoolId, studentId), params),
      null,
      null
    );
    return this.handleResponse<Page<LessonsV3.LessonBundle>>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((page) => normalizeLessonBundlesPage(page)));
  }

  createStudentBundle(
    schoolId: number,
    studentId: number,
    details: LessonsV3.LessonBundleDetails
  ): Observable<LessonsV3.LessonBundle> {
    this.logger.log(`(v3) creating student bundle schoolId=${schoolId}, studentId=${studentId}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.POST,
      this.studentBundlesUrl(schoolId, studentId),
      null,
      JSON.stringify(details)
    );
    return this.handleResponse<LessonsV3.LessonBundle>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((bundle) => normalizeLessonBundle(bundle)));
  }

  updateStudentBundleExpiry(
    schoolId: number,
    studentId: number,
    bundleId: number,
    expiryDate: Date
  ): Observable<void> {
    this.logger.log(`(v3) updating student bundle expiry schoolId=${schoolId}, studentId=${studentId}, bundleId=${bundleId}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PATCH,
      this.studentBundlesUrl(schoolId, studentId, `/${bundleId}/expiry-date`),
      null,
      JSON.stringify(expiryDate)
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getStudentCommits(
    schoolId: number,
    studentId: number,
    pageable?: Pageable | null,
    productCode?: string | null,
    courseCode?: string | null,
    commitStatuses?: LessonsV3.LessonCommitStatus[] | null,
    lessonStatuses?: LessonsV3.LessonStatus[] | null
  ): Observable<Page<LessonsV3.LessonCommit>> {
    this.logger.log(`(v3) getting student commits schoolId=${schoolId}, studentId=${studentId}`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'productCode', productCode);
    params = appendIfDefined(params, 'courseCode', courseCode);
    params = this.appendAll(params, 'commitStatuses', commitStatuses ?? null);
    params = this.appendAll(params, 'lessonStatuses', lessonStatuses ?? null);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.studentCommitsUrl(schoolId, studentId), params),
      null,
      null
    );
    return this.handleResponse<Page<LessonsV3.LessonCommit>>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((page) => normalizeLessonCommitsPage(page)));
  }

  getStudents(
    schoolId?: number | null,
    personId?: number | null,
    email?: string | null,
    studentIds?: number[] | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.V3SchoolStudent>> {
    this.logger.log(`(v3) getting casa students perspective`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'schoolId', schoolId);
    params = appendIfDefined(params, 'personId', personId);
    params = appendIfDefined(params, 'email', email);
    params = this.appendAll(params, 'studentIds', (studentIds ?? []).map((id) => `${id}`));
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params('/v1/students', params),
      null,
      null
    );
    return this.handleResponse<Page<LessonsV3.V3SchoolStudent>>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  getSchoolProducts(schoolId: number): Observable<LessonsV3.V3Product[]> {
    this.logger.log(`(v3) getting school products schoolId=${schoolId}`);
    const request = new HttpRequest(Platform.Events, HttpMethod.GET, this.schoolProductsUrl(schoolId), null, null);
    return this.handleResponse<LessonsV3.V3Product[]>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getProductVariants(
    view?: string | null,
    platform?: string | null,
    variantCodes?: string[] | null,
    lang?: string | null,
    currency?: string | null,
    priceLists?: string[] | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.ProductVariantPreview>> {
    this.logger.log(`(v3) getting product variants`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'view', view);
    params = appendIfDefined(params, 'platform', platform);
    params = this.appendAll(params, 'variantCode', variantCodes ?? null);
    params = appendIfDefined(params, 'lang', lang);
    params = appendIfDefined(params, 'currency', currency);
    params = this.appendAll(params, 'priceList', priceLists ?? null);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params('/v1/product-variants', params),
      null,
      null
    );
    return this.handleResponse<Page<LessonsV3.ProductVariantPreview>>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  getProductCourses(
    schoolId: number,
    productCode: string
  ): Observable<LessonsV3.Course[]> {
    this.logger.log(`(v3) getting product courses schoolId=${schoolId}, productCode=${productCode}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.schoolProductsUrl(schoolId, `/${productCode}/courses`),
      null,
      null
    );
    return this.handleResponse<LessonsV3.Course[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  getCourseCompetences(
    schoolId: number,
    productCode: string,
    courseCode: string
  ): Observable<LessonsV3.Competence[]> {
    this.logger.log(`(v3) getting course competences schoolId=${schoolId}, productCode=${productCode}, courseCode=${courseCode}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.schoolProductsUrl(schoolId, `/${productCode}/courses/${courseCode}/competences`),
      null,
      null
    );
    return this.handleResponse<LessonsV3.Competence[]>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getCompetenceMappings(): Observable<LessonsV3.CompetenceMapping[]> {
    this.logger.log(`(v3) getting competence mappings`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      '/v1/products/competence-mappings',
      null,
      null
    );
    return this.handleResponse<LessonsV3.CompetenceMapping[]>(
      this.bridge.callForResponse<HttpResponse>('exchange', { request })
    );
  }

  getProvaContext(
    schoolId: number,
    studentId: number,
    productCode: string
  ): Observable<LessonsV3.ColProvaContextDetails> {
    this.logger.log(`(v3) getting prova context schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.productContextsUrl(schoolId, studentId, productCode, '/prova-context'),
      null,
      null
    );
    return this.handleResponse<LessonsV3.ColProvaContextDetails>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((context) => normalizeProvaContext(context)));
  }

  updateProvaContext(
    schoolId: number,
    studentId: number,
    productCode: string,
    context: LessonsV3.ColProvaContextDetails
  ): Observable<LessonsV3.ColProvaContextDetails> {
    this.logger.log(`(v3) updating prova context schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PUT,
      this.productContextsUrl(schoolId, studentId, productCode, '/prova-context'),
      null,
      JSON.stringify(context)
    );
    return this.handleResponse<LessonsV3.ColProvaContextDetails>(this.bridge.callForResponse<HttpResponse>('exchange', { request }))
      .pipe(map((updated) => normalizeProvaContext(updated)));
  }

  getProductContexts(
    schoolId: number,
    studentId: number,
    productCode?: string | null
  ): Observable<LessonsV3.ProductContextDetails[]> {
    this.logger.log(`(v3) getting product contexts schoolId=${schoolId}, studentId=${studentId}`);
    const params = appendIfDefined(new HttpParams(), 'productCode', productCode);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.productContextsUrl(schoolId, studentId), params),
      null,
      null
    );
    return this.handleResponse<LessonsV3.ProductContextDetails[]>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  updateProductContext(
    schoolId: number,
    studentId: number,
    productCode: string,
    context: LessonsV3.ProductContextDetails
  ): Observable<void> {
    this.logger.log(`(v3) updating product context schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.PUT,
      this.productContextsUrl(schoolId, studentId, productCode),
      null,
      JSON.stringify(context)
    );
    return this.handleResponse<void>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }

  getCourseContexts(
    schoolId: number,
    studentId: number,
    productCode: string,
    current?: boolean | null
  ): Observable<LessonsV3.ColCourseContext[]> {
    this.logger.log(`(v3) getting course contexts schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    const params = appendIfDefined(new HttpParams(), 'current', current);
    const request = new HttpRequest(
      Platform.Events,
      HttpMethod.GET,
      this.params(this.productContextsUrl(schoolId, studentId, productCode, '/course-contexts'), params),
      null,
      null
    );
    return this.handleResponse<LessonsV3.ColCourseContext[]>(this.bridge.callForResponse<HttpResponse>('exchange', { request }));
  }
}
